import { ShieldCheck, FileText, Users, Scale, BookOpen, Mail } from "lucide-react";
import { FadeIn, Kicker, SectionShell, MaskedLine } from "../components/Reveal";
import { useI18n } from "../lib/i18n";
import SEO from "../components/SEO";

export default function Transparency() {
  const { d } = useI18n();
  const t = d.transparency;

  return (
    <main>
      <SEO page="transparency" />
      <section className="pt-36 pb-20" data-testid="transparency-hero">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <Kicker>{t.kicker}</Kicker>
          <h1 className="mt-8 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl" data-testid="transparency-headline">
            <MaskedLine delay={0.2}>{t.h1a}</MaskedLine>
            <MaskedLine delay={0.35}><span className="font-serif font-medium normal-case italic text-crimson dark:text-stalk">{t.h1b}</span></MaskedLine>
          </h1>
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground">{t.para}</p>
          </FadeIn>
        </div>
      </section>

      <SectionShell id="organization" className="border-t border-border bg-card/40">
        <FadeIn>
          <div className="flex items-center gap-3"><Scale size={20} className="text-crimson dark:text-stalk" /><Kicker>{t.org.kicker}</Kicker></div>
        </FadeIn>
        <div className="mt-10 grid gap-px border border-border bg-border md:grid-cols-2" data-testid="organization-facts">
          {t.org.facts.map(([k, v], i) => (
            <FadeIn key={k} delay={i * 0.05} className="bg-background p-6" data-testid={`org-fact-${i}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{k}</p>
              <p className="mt-2 font-serif text-xl">{v}</p>
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="governance">
        <FadeIn>
          <div className="flex items-center gap-3"><Users size={20} className="text-crimson dark:text-stalk" /><Kicker>{t.gov.kicker}</Kicker></div>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">{t.gov.para}</p>
        </FadeIn>
        <div className="mt-10 border border-border" data-testid="governance-table">
          {t.gov.roles.map((g, i) => (
            <FadeIn key={g.role} delay={i * 0.05} className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background px-6 py-5 last:border-0" data-testid={`governance-role-${i}`}>
              <div>
                <p className="font-display text-base font-semibold">{g.role}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{g.holder}</p>
              </div>
              <span className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${g.filled ? "border-foliage/50 text-foliage dark:border-stalk/60 dark:text-stalk" : "border-border text-muted-foreground"}`}>
                {g.filled ? t.gov.filled : t.gov.vacant}
              </span>
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="documents" className="border-t border-border bg-card/40">
        <FadeIn>
          <div className="flex items-center gap-3"><FileText size={20} className="text-crimson dark:text-stalk" /><Kicker>{t.docs.kicker}</Kicker></div>
        </FadeIn>
        <div className="mt-10 border border-border" data-testid="documents-list">
          {t.docs.items.map((doc, i) => (
            <FadeIn key={doc.name} delay={i * 0.04} className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background px-6 py-4 last:border-0" data-testid={`document-row-${i}`}>
              <div>
                <p className="font-display text-sm font-semibold">{doc.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{doc.note}</p>
              </div>
              <span className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${doc.status === "Published" || doc.status === "公開済み" || doc.status === "Opublikowane" ? "border-foliage/50 text-foliage dark:border-stalk/60 dark:text-stalk" : "border-gold/50 text-gold"}`}>
                {doc.status}
              </span>
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="manifesto" className="border-t border-border">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <FadeIn>
              <div className="flex items-center gap-3"><ShieldCheck size={20} className="text-crimson dark:text-stalk" /><Kicker>{t.manifesto.kicker}</Kicker></div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">{t.manifesto.title}</h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">{t.manifesto.para}</p>
            </FadeIn>
          </div>
          <div className="space-y-0 border border-border">
            {t.manifesto.principles.map((p, i) => (
              <FadeIn key={p} delay={i * 0.06} className="flex gap-4 border-b border-border bg-background p-5 last:border-0" data-testid={`principle-${i}`}>
                <span className="font-serif text-2xl italic text-crimson dark:text-stalk">{i + 1}.</span>
                <p className="text-sm leading-relaxed text-foreground/85">{p}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </SectionShell>

      <SectionShell id="models" className="border-t border-border bg-moss text-parchment dark:bg-nightcard">
        <FadeIn>
          <div className="flex items-center gap-3"><BookOpen size={20} className="text-gold" /><Kicker className="text-gold">{t.models.kicker}</Kicker></div>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-parchment/75">{t.models.para}</p>
        </FadeIn>
        <div className="mt-10 grid gap-px border border-parchment/15 bg-parchment/15 sm:grid-cols-2" data-testid="reference-models">
          {t.models.items.map((m, i) => (
            <FadeIn key={m.name} delay={i * 0.06} className="bg-moss p-7 dark:bg-nightcard" data-testid={`model-${i}`}>
              <h3 className="font-display text-lg font-semibold">{m.name}</h3>
              <p className="mt-2 text-sm text-parchment/70">{m.desc}</p>
              <a href={m.url} target="_blank" rel="noreferrer" className="u-link mt-4 inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                {t.models.visit}
              </a>
            </FadeIn>
          ))}
        </div>
      </SectionShell>

      <SectionShell id="contact" className="border-t border-border">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <FadeIn>
            <div className="flex items-center gap-3"><Mail size={20} className="text-crimson dark:text-stalk" /><Kicker>{t.contact.kicker}</Kicker></div>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground">{t.contact.para}</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <a href="/#join" data-testid="transparency-contact-cta" className="inline-block border border-crimson bg-crimson px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-parchment transition-colors hover:bg-transparent hover:text-crimson">
              {t.contact.cta}
            </a>
          </FadeIn>
        </div>
      </SectionShell>
    </main>
  );
}
